/**
 * ProcessBar - Thin collapsible bar showing research process summary.
 *
 * After research completes, this sits above the Decision Sheet.
 * Collapsed: compact 48px bar with phase/agent/task metadata + chevron.
 * Expanded: renders the full streaming view (LogicTree, etc.) as children.
 */

import { useState, useRef, useEffect, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown, Eye, Clock } from 'lucide-react';

export interface ProcessBarMetadata {
  phaseCount?: number;
  agentCount?: number;
  taskCount?: number;
  durationSeconds?: number;
  debateRounds?: number;
  label?: string;
}

interface ProcessBarProps {
  children: ReactNode;
  metadata: ProcessBarMetadata;
  defaultExpanded?: boolean;
  /** When true, starts expanded then auto-collapses after 500ms */
  animateCollapse?: boolean;
  className?: string;
}

export function ProcessBar({
  children,
  metadata,
  defaultExpanded = false,
  animateCollapse = false,
  className,
}: ProcessBarProps) {
  const [isExpanded, setIsExpanded] = useState(animateCollapse ? true : defaultExpanded);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  // Auto-collapse after 500ms when animateCollapse is set
  useEffect(() => {
    if (animateCollapse) {
      const timer = setTimeout(() => setIsExpanded(false), 500);
      return () => clearTimeout(timer);
    }
  }, [animateCollapse]);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [isExpanded, children]);

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const metaParts: string[] = [];
  if (metadata.phaseCount) metaParts.push(`${metadata.phaseCount} phases`);
  if (metadata.agentCount) metaParts.push(`${metadata.agentCount} agents`);
  if (metadata.taskCount) metaParts.push(`${metadata.taskCount} tasks`);
  if (metadata.debateRounds) metaParts.push(`${metadata.debateRounds} debate rounds`);

  return (
    <div className={cn('w-full', className)}>
      {/* Collapsed bar — always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          'w-full flex items-center gap-3 px-4 py-3',
          'bg-surface border border-border rounded-lg',
          'hover:bg-surface-light hover:border-border-light',
          'transition-colors duration-200 cursor-pointer',
          'group',
        )}
      >
        <Eye className="w-4 h-4 text-text-muted flex-shrink-0" />

        <span className="text-xs text-text-muted font-medium uppercase tracking-wider">
          {metadata.label ?? 'Analysis Process'}
        </span>

        <span className="text-xs text-text-muted">
          {metaParts.join(' · ')}
        </span>

        {metadata.durationSeconds != null && (
          <span className="flex items-center gap-1 text-xs text-text-muted ml-auto mr-2">
            <Clock className="w-3 h-3" />
            {formatDuration(metadata.durationSeconds)}
          </span>
        )}

        <ChevronDown
          className={cn(
            'w-4 h-4 text-text-muted flex-shrink-0 transition-transform duration-300',
            isExpanded && 'rotate-180',
          )}
        />
      </button>

      {/* Expandable content — renders children (the streaming view snapshot) */}
      <div
        ref={contentRef}
        className="overflow-hidden transition-all duration-500 ease-in-out"
        style={{
          maxHeight: isExpanded ? `${contentHeight}px` : '0px',
          opacity: isExpanded ? 1 : 0,
        }}
      >
        <div className="mt-2 border border-border rounded-xl overflow-hidden">
          {children}
        </div>
      </div>
    </div>
  );
}
